import { Select, SelectOption } from '@bloomfieldrobotics/canopy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { translateNumDays } from '../../../services/translation/impl/i18n/utils/translateNumDays';
import { StrictDateRange } from './types';
import {
  DateRangeKey,
  generateDateRangeFilter,
} from './utils/generateDateRangeFilter';
/**
 * The value of the first date range in the menu, if you are using the default options
 */
export const DEFAULT_DATE_RANGE_FILTER_SELECT_VALUE = 'week';

type ControlledProps = {
  value: DateRangeKey;
  onChange: (dateRange: StrictDateRange | null, newValue: DateRangeKey) => void;
  initialValue?: never;
};

type UncontrolledProps = {
  initialValue?: DateRangeKey;
  onChange?: (dateRange: StrictDateRange | null) => void;
  value?: never;
};

type Props = (ControlledProps | UncontrolledProps) & {
  options?: DateRangeKey[];
};

/**
 * Customizable Date Range Select with default options
 * Can be controlled or uncontrolled
 *
 * @param {DateRangeKey} initialValue - initial value, if uncontrolled
 * @param {DateRangeKey} value - controlled input value
 * @param onChange - function to set the calculated date range, and handle controlled input value change.
 * @param {DateRangeKey[]} options - (optional) array of option keys to display instead of the default options
 *
 * @returns Date Range select component
 */
export const DateRangeFilterSelect = ({
  value,
  onChange,
  initialValue = 'week',
  options = ['week', '30days', '90days', '180days', '365days', 'all'],
}: Props) => {
  const dateRangeMenuItems = useDateRangeFilterOptions(options);

  const [internalVal, setInternalVal] = useState(value ?? initialValue);

  const handleChange = useCallback(
    (selectedValue: DateRangeKey) => {
      if (onChange) {
        const range = generateDateRangeFilter(selectedValue);
        onChange(range, selectedValue);
      }
      setInternalVal(selectedValue);
    },
    [onChange]
  );

  useEffect(() => {
    if (!value) {
      handleChange(initialValue);
    }
  }, [handleChange, initialValue, value]);

  return (
    <Select
      options={dateRangeMenuItems}
      value={value ?? internalVal}
      onChange={handleChange}
    />
  );
};

const useDateRangeFilterOptions = (
  optionKeys: DateRangeKey[]
): SelectOption<DateRangeKey>[] => {
  const { t } = useTranslation();

  const filterMenuItems = useMemo(() => {
    // Default options map:
    const optionsMap = {
      week: { value: 'week', label: t('Week') },
      '30days': { value: '30days', label: translateNumDays(30, t) },
      '90days': { value: '90days', label: translateNumDays(90, t) },
      '180days': { value: '180days', label: translateNumDays(180, t) },
      '365days': { value: '365days', label: translateNumDays(365, t) },
      all: { value: 'all', label: t('All') },
    };

    return optionKeys.map(
      (key) => optionsMap[key]
    ) as SelectOption<DateRangeKey>[];
  }, [t, optionKeys]);

  return filterMenuItems;
};
